import Link from 'next/link';

import { Text } from '@blockworks/ui';
import { RenderMarkdown } from '@blockworks/ui/legacy/html';
import { cn } from '@blockworks/ui/utils';
import { DashboardGrid, DashboardGridProviderProps, PreloadedDashboardData } from '@blockworks/viz/dashboard-grid';

import AdContainer from '@/components/ads/ad-container';
import AdContainerIds from '@/components/ads/ad-container-ids';
import { NoResultsFound } from '@/components/common/no-results-found';
import Newsletter from '@/components/pages/frontpage/components/feature/right/newsletter';
import NewsRecirculationSection from '@/components/sections/content/news-recirculation-section';
import subscribeToNewsletter from '@/utils/functions/subscribe-to-newsletter';

import { DashboardPageProps } from '../utils/get-dashboard-props';

const DashboardPageLayout = ({
    datapageName,
    dashboardSlug,
    datapageSlug,
    datapageDashboardTitle,
    datapageDashboardDescriptionContent,
    datapageDescription,
    datapageDashboards,
    isPublic,
    layouts,
    visualizations,
    preloadedDataProp,
}: DashboardPageProps) => {
    // if preloaded data fails or returns undefined, we don't want to show dashboard
    let preloadedData: PreloadedDashboardData | null;
    try {
        preloadedData = DashboardGrid.decompressData<PreloadedDashboardData>(preloadedDataProp) ?? null;
    } catch (error) {
        console.error('Failed to decompress dashboard data:', error);
        preloadedData = null;
    }

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-col flex-wrap justify-between gap-6 p-10">
                <div className="flex flex-col">
                    <h1 className="flex justify-start font-bold tracking-[0.25px] text-xl lg:text-2xl xl:text-[32px] text-gray-900">
                        {datapageName}:&nbsp;<span className="font-medium">{datapageDashboardTitle}</span>
                    </h1>
                    <p className="mt-4 text-md">
                        {datapageDescription ?? `Onchain metrics, activity and charts for ${datapageName}.`}
                    </p>
                </div>

                <nav className="flex justify-start gap-2 flex-wrap">
                    {datapageDashboards &&
                        datapageDashboards.length > 0 &&
                        datapageDashboards.map(item => (
                            <Link
                                prefetch={false}
                                key={item.title}
                                href={item.href}
                                className={cn(
                                    'inline-flex items-center font-bold uppercase whitespace-nowrap leading-4 tracking-[1.25px] py-2 px-4 text-xs border border-brand',
                                    item.slug === dashboardSlug
                                        ? 'bg-brand text-white border-brand hover:opacity-80'
                                        : 'text-brand hover:bg-brand hover:border-brand border-brand hover:text-white',
                                )}
                            >
                                {item.title}
                            </Link>
                        ))}
                </nav>
            </div>

            <div className="px-6 pb-6">
                {preloadedData ? (
                    <DashboardGrid.Provider
                        mode={DashboardGrid.Mode.Preloaded}
                        showAllItems={isPublic}
                        preloadedData={preloadedData}
                        layouts={layouts}
                        visualizations={visualizations as DashboardGridProviderProps['visualizations']}
                        componentSlots={{
                            VisualizationTitle: ({ title, slug }) => {
                                return (
                                    <Text as="span" lines={1} truncate>
                                        <Link
                                            prefetch={false}
                                            href={`/analytics/${datapageSlug}/${dashboardSlug}/${slug}`}
                                        >
                                            <Text as={'h2'} size="xs" weight="semibold" lines={1} truncate>
                                                {title}
                                            </Text>
                                        </Link>
                                    </Text>
                                );
                            },
                        }}
                    >
                        <DashboardGrid />
                    </DashboardGrid.Provider>
                ) : (
                    <NoResultsFound
                        title="Something Went Wrong"
                        description="We're sorry, an unexpected error occurred. Please try again later."
                        className="min-h-[350px] border"
                    />
                )}
            </div>
            {datapageDashboardDescriptionContent && (
                <>
                    <hr className="border-gray-200" />
                    <section className="relative flex flex-row flex-wrap lg:flex-nowrap">
                        {/* vertical border */}
                        <div className="hidden lg:block absolute top-0 bottom-0 left-[66.66%] w-px bg-gray-200 z-10" />

                        <div className="basis-full lg:basis-4/6 border-b lg:border-b-0">
                            <div className="p-10">
                                <RenderMarkdown
                                    className="prose max-w-none"
                                    text={datapageDashboardDescriptionContent}
                                />
                            </div>
                            <NewsRecirculationSection
                                algoliaSearchKeyword={'analytics'}
                                title={`latest analytics news`}
                                count={16}
                                paddingClassName="p-10"
                                className="p-0"
                                imageQuality={10}
                            />
                        </div>

                        <div className="basis-full lg:basis-2/6 lg:min-w-[300px]">
                            <Newsletter
                                title="Newsletter"
                                subTitle="The Breakdown"
                                description="Decoding crypto and the markets. Daily, with Byron Gilliam."
                                inputPlaceholder="Email address"
                                buttonText="Subscribe"
                                onButtonClick={subscribeToNewsletter}
                            />

                            <NewsRecirculationSection
                                algoliaSearchKeyword={datapageName}
                                title={`${datapageName} in the news`}
                                style="vertical"
                                count={2}
                                paddingClassName="p-10"
                                className="p-0"
                            />

                            <div className="p-6 xl:p-12">
                                <AdContainer
                                    googleAd={AdContainerIds.NewsArticleRight600x300!}
                                    className="hidden lg:flex h-[600px] w-[300px] text-center align-middle mx-auto"
                                />
                            </div>

                            <NewsRecirculationSection
                                algoliaSearchKeyword={datapageName}
                                style="vertical"
                                count={2}
                                page={1}
                                paddingClassName="p-10"
                                className="p-0"
                            />
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

export default DashboardPageLayout;
